<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency'
import EmptyState from '@/Components/UI/EmptyState.vue'

const { t } = useI18n()
const { formatDate } = useCurrency()

const props = defineProps({
  range:       { type: Object, required: true },
  rentals:     { type: Array,  required: true },
  maintenance: { type: Array,  required: true },
  vehicles:    { type: Array,  required: true },
  window:      { type: [Number, String], required: true },
})

const startDate = computed(() => new Date(props.range.from))
const days      = computed(() => Number(props.window))

const dayCells = computed(() => {
  const out = []
  for (let i = 0; i < days.value; i++) {
    const d = new Date(startDate.value)
    d.setDate(d.getDate() + i)
    out.push({
      date:    d,
      label:   formatDate(d, { weekday: 'short' }),
      day:     d.getDate(),
      isToday: isSameDay(d, new Date()),
    })
  }
  return out
})

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function dayIndex(iso) {
  const d = new Date(iso)
  return Math.floor((d.setHours(0, 0, 0, 0) - new Date(startDate.value).setHours(0, 0, 0, 0)) / 86_400_000)
}

function clip(start, end) {
  const s = Math.max(0, Math.min(days.value, dayIndex(start)))
  const e = Math.max(0, Math.min(days.value, dayIndex(end) + 1))
  return { dayStart: s, daySpan: Math.max(1, e - s) }
}

function leftPct(dayStart) { return (dayStart / days.value) * 100 }
function widthPct(daySpan) { return (daySpan / days.value) * 100 }

function packLanes(bars) {
  const sorted = [...bars].sort((a, b) => a.dayStart - b.dayStart)
  const laneEnds = [] // laneEnds[i] = exclusive end day of last bar in lane i
  for (const bar of sorted) {
    let lane = laneEnds.findIndex(end => end <= bar.dayStart)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(0)
    }
    bar.slot = lane
    laneEnds[lane] = bar.dayStart + bar.daySpan
  }
  return Math.max(1, laneEnds.length)
}

const rowsActive = computed(() => {
  const byVehicle = new Map()
  const ensure = (vid) => {
    if (!byVehicle.has(vid)) {
      const v = props.vehicles.find(x => x.id === vid)
      if (!v) return null
      byVehicle.set(vid, { vehicle: v, bars: [] })
    }
    return byVehicle.get(vid)
  }
  for (const r of props.rentals) {
    const { dayStart, daySpan } = clip(r.bar_start_at, r.bar_end_at)
    if (daySpan < 1) continue
    const row = ensure(r.vehicle_id); if (!row) continue
    row.bars.push({
      kind: 'rental', key: `r-${r.id}`, dayStart, daySpan,
      color: r.color_token, href: r.href,
      label: r.customer_name,
      title: `${r.customer_name} · ${formatDate(r.bar_start_at, { month: 'short', day: 'numeric' })} – ${formatDate(r.bar_end_at, { month: 'short', day: 'numeric' })}`,
    })
  }
  for (const m of props.maintenance) {
    const end = m.ended_at || m.planned_end || props.range.to
    const { dayStart, daySpan } = clip(m.started_at, end)
    if (daySpan < 1) continue
    const row = ensure(m.vehicle_id); if (!row) continue
    row.bars.push({
      kind: 'maintenance', key: `m-${m.id}`, dayStart, daySpan,
      color: '--warn', href: m.href,
      label: t('calendar.maintenance'),
      title: t('calendar.maintenance'),
    })
  }
  const result = Array.from(byVehicle.values())
  for (const row of result) {
    row.lanes = packLanes(row.bars)
  }
  return result
})

const idleVehicles = computed(() => {
  const activeIds = new Set(rowsActive.value.map(r => r.vehicle.id))
  return props.vehicles.filter(v => !activeIds.has(v.id))
})

const isEmpty = computed(() => rowsActive.value.length === 0 && idleVehicles.value.length === 0)
</script>

<template>
  <EmptyState v-if="isEmpty" type="success" :title="t('calendar.empty.fleet')" />

  <div
    v-else
    class="overflow-x-auto rounded-[var(--radius-table)] border border-[color:var(--line)] bg-[color:var(--surface)]"
  >
    <div
      class="grid"
      :style="{ gridTemplateColumns: `12rem repeat(${days}, minmax(2.5rem, 1fr))` }"
    >
      <div class="px-3 py-2 sticky left-0 bg-[color:var(--surface)] z-10"></div>
      <div
        v-for="cell in dayCells"
        :key="`${cell.label}-${cell.day}`"
        class="px-1 py-2 text-center text-xs border-l border-[color:var(--line)]"
        :class="cell.isToday ? 'bg-[color:var(--accentSoft)]' : ''"
      >
        <div class="text-[10px] uppercase tracking-wider text-[color:var(--muted)]">{{ cell.label }}</div>
        <div class="fw-value tabular-nums text-[color:var(--ink)]">{{ cell.day }}</div>
      </div>

      <template v-for="row in rowsActive" :key="row.vehicle.id">
        <div class="px-3 py-3 text-sm text-[color:var(--ink)] sticky left-0 bg-[color:var(--surface)] z-10 border-t border-[color:var(--line)] truncate">
          {{ row.vehicle.label }}<span v-if="row.vehicle.registration" class="text-[color:var(--muted)]"> · {{ row.vehicle.registration }}</span>
        </div>
        <div :style="{ gridColumn: `2 / span ${days}`, minHeight: `calc(0.5rem + ${row.lanes} * 2rem + 0.5rem)` }" class="border-t border-[color:var(--line)] relative py-2">
          <Link
            v-for="bar in row.bars"
            :key="bar.key"
            :href="bar.href"
            :title="bar.title"
            class="absolute h-7 rounded-[var(--radius-button)] px-2 text-xs flex items-center truncate text-[color:var(--bg)]"
            :style="{
              left:   `calc(${leftPct(bar.dayStart)}% + 2px)`,
              width:  `calc(${widthPct(bar.daySpan)}% - 4px)`,
              top:    `calc(0.5rem + ${bar.slot} * 2rem)`,
              background: bar.kind === 'maintenance'
                ? `repeating-linear-gradient(45deg, var(${bar.color}), var(${bar.color}) 4px, color-mix(in oklch, var(${bar.color}) 70%, black) 4px, color-mix(in oklch, var(${bar.color}) 70%, black) 8px)`
                : `var(${bar.color})`,
            }"
          >
            <span class="truncate">{{ bar.label }}</span>
          </Link>
        </div>
      </template>

      <template v-for="v in idleVehicles" :key="`idle-${v.id}`">
        <div class="px-3 py-3 text-sm text-[color:var(--inkSoft)] sticky left-0 bg-[color:var(--surface)] z-10 border-t border-[color:var(--line)] truncate">
          {{ v.label }}<span v-if="v.registration" class="text-[color:var(--muted)]"> · {{ v.registration }}</span>
        </div>
        <div :style="{ gridColumn: `2 / span ${days}` }" class="border-t border-[color:var(--line)] py-3 px-3 text-xs text-[color:var(--muted)]">—</div>
      </template>
    </div>
  </div>
</template>
